import type { ApiResponse, PagedResponseDto } from "@/types/common";
import type {
  AdminProductCreateDto,
  ProductCreateDto,
  ProductListDto,
  ProductApprovalPayload,
  ProductResponseDto,
  ProductSearchParams,
  ProductUpdateDto,
} from "@/types/product.dto";
import { apiClient, unwrapApiResponse } from "./httpClient";

export const productsService = {
  search(params: ProductSearchParams): Promise<PagedResponseDto<ProductListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<ProductListDto>>>("/Products", { params }),
    );
  },

  getFeatured(limit = 10): Promise<ProductListDto[]> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<ProductListDto[]>>("/Products/featured", { params: { limit } }),
    );
  },

  getById(id: number): Promise<ProductResponseDto> {
    return unwrapApiResponse(apiClient.get<ApiResponse<ProductResponseDto>>(`/Products/${id}`));
  },

  getByStore(storeId: number, page = 1, pageSize = 20): Promise<PagedResponseDto<ProductListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<ProductListDto>>>(`/Products/store/${storeId}`, {
        params: { page, pageSize },
      }),
    );
  },

  getByCategory(categoryId: number, page = 1, pageSize = 20): Promise<PagedResponseDto<ProductListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<ProductListDto>>>(`/Products/category/${categoryId}`, {
        params: { page, pageSize },
      }),
    );
  },

  getPending(page = 1, pageSize = 20): Promise<PagedResponseDto<ProductListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<ProductListDto>>>("/Products/pending", {
        params: { page, pageSize },
      }),
    );
  },

  getAdminAll(page = 1, pageSize = 200): Promise<PagedResponseDto<ProductListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<ProductListDto>>>("/Products/admin", {
        params: { page, pageSize },
      }),
    );
  },

  approve(id: number, payload: ProductApprovalPayload): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(`/Products/${id}/approve`, payload),
    );
  },

  setVisibility(id: number, isActive: boolean): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<boolean>>(`/Products/${id}/visibility`, { isActive }),
    );
  },

  deleteByAdmin(id: number): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.delete<ApiResponse<boolean>>(`/Products/${id}/admin`),
    );
  },

  create(storeId: number, dto: ProductCreateDto): Promise<ProductResponseDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<ProductResponseDto>>(`/Products/store/${storeId}`, dto),
    );
  },

  update(id: number, storeId: number, dto: ProductUpdateDto): Promise<ProductResponseDto> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<ProductResponseDto>>(`/Products/${id}/store/${storeId}`, dto),
    );
  },

  createByAdmin(dto: AdminProductCreateDto): Promise<ProductResponseDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<ProductResponseDto>>("/Products/admin", dto),
    );
  },

  updateByAdmin(id: number, dto: ProductUpdateDto): Promise<ProductResponseDto> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<ProductResponseDto>>(`/Products/${id}/admin`, dto),
    );
  },

  delete(id: number, storeId: number): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.delete<ApiResponse<boolean>>(`/Products/${id}/store/${storeId}`),
    );
  },

  getLowStock(storeId: number): Promise<ProductListDto[]> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<ProductListDto[]>>(`/Products/store/${storeId}/low-stock`),
    );
  },
};
